import { getEventCatalogFeed, loadEventsByIds, type EventSourceResult } from './eventService'
import { defaultDataSourceConfigs } from '../data/dataSources'
import { loadDataSourceConfigs } from '../storage/localConfig'
import { loadDatasetFreshness } from './freshnessService'
import type { PredictionEventCatalog } from '../types/event'
import type { CurrentFreshness } from '../types/freshness'
import type { PredictionCategory } from '../types/prediction'
import type {
  ChangeDirection,
  ConsensusState,
  PresentationCatalogManifest,
  PresentationCatalogPage,
  PresentationDetailsIndex,
  PresentationDetailsPage,
  PresentationEventRecord,
  PresentationLocale,
  PresentationLocaleBundle,
  PresentationHomeBundle,
  PresentationOutcome,
  PresentationRankingEntry,
  PresentationRankingPayload,
  PresentationRankingType,
  PresentationSearchBundle,
  PresentationSource,
  PresentationSourceStatus,
  PredictionViewModel,
} from '../types/presentation'
import { categoryLabel, tForLocale } from '../i18n/locale'
import {
  buildDisplaySemanticKey,
  ensureUniquePresentationTitles,
  inferOutcomeStructure,
  localDisplayOutcomeLabel,
  outcomeSourceTitle,
  outcomeStructureLabel,
  outcomeStructureNote,
  presentationTitleForEvent,
} from '../presentation/semantics'
import { formatEndTime, formatPercentagePoints, formatProbability, formatUpdatedAt } from '../utils/format'
import { isDeadlinePassed, sourceFreshnessStatus, sourceFreshnessStatusLabel } from '../utils/freshness'

const PRESENTATION_RANKING_PATH = '/data/presentation/ranking-v2.json'
const PRESENTATION_HOME_PATH = '/data/presentation/home.json'
const PRESENTATION_CATALOG_PATH = '/data/presentation/catalog/index.json'
const PRESENTATION_DETAILS_PATH = '/data/presentation/details/index.json'
const PRESENTATION_SEARCH_PATH = '/data/presentation/search.json'
const PRESENTATION_PAGE_SIZE = 30

let rankingPromise: Promise<PresentationRankingPayload> | null = null
let homeBundlePromise: Promise<PresentationHomeBundle> | null = null
let catalogManifestPromise: Promise<PresentationCatalogManifest> | null = null
let detailsIndexPromise: Promise<PresentationDetailsIndex> | null = null
let searchBundlePromise: Promise<PresentationSearchBundle> | null = null
const catalogPagePromises = new Map<string, Promise<PresentationCatalogPage>>()
const detailsPagePromises = new Map<string, Promise<PresentationDetailsPage>>()
const homeFeedPromises = new Map<string, Promise<PresentationHomeFeed>>()
const catalogFeedPromises = new Map<string, Promise<PresentationCatalogFeed>>()
const detailPromises = new Map<string, Promise<PresentationDetail | null>>()
const localeBundlePromises = new Map<PresentationLocale, Promise<PresentationLocaleBundle | null>>()

class PresentationDataError extends Error {
  status: number

  constructor(status: number, path: string) {
    super(`展示数据请求失败（${status}）：${path}`)
    this.name = 'PresentationDataError'
    this.status = status
  }
}

function isNotFound(error: unknown) {
  return error instanceof PresentationDataError && error.status === 404
}

async function fetchJson<T>(path: string, options: { forceRefresh?: boolean } = {}) {
  const response = await fetch(path, {
    cache: options.forceRefresh ? 'no-store' : 'default',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new PresentationDataError(response.status, path)
  return response.json() as Promise<T>
}

export function loadPresentationRanking(options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || rankingPromise === null) {
    rankingPromise = fetchJson<PresentationRankingPayload>(PRESENTATION_RANKING_PATH, options)
  }
  return rankingPromise
}

function readPresentationLocaleBundle(payload: unknown, locale: PresentationLocale): PresentationLocaleBundle | null {
  if (typeof payload !== 'object' || payload === null || Array.isArray(payload)) return null
  const bundle = payload as Partial<PresentationLocaleBundle>
  if (
    bundle.schemaVersion !== 1
    || bundle.locale !== locale
    || typeof bundle.generatedAt !== 'string'
    || typeof bundle.events !== 'object'
    || bundle.events === null
    || Array.isArray(bundle.events)
  ) return null
  return bundle as PresentationLocaleBundle
}

function readSessionLocaleBundle(locale: PresentationLocale) {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.sessionStorage.getItem(`yujian:presentation-locale:${locale}`)
    return raw ? readPresentationLocaleBundle(JSON.parse(raw), locale) : null
  } catch {
    return null
  }
}

function writeSessionLocaleBundle(locale: PresentationLocale, bundle: PresentationLocaleBundle) {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(`yujian:presentation-locale:${locale}`, JSON.stringify(bundle))
  } catch {
    // Session storage is an optimization; the network bundle remains authoritative.
  }
}

export function loadPresentationLocaleBundle(locale: PresentationLocale = 'zh-CN', options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh) {
    localeBundlePromises.delete(locale)
    try {
      window.sessionStorage.removeItem(`yujian:presentation-locale:${locale}`)
    } catch {
      // Ignore unavailable storage.
    }
  }
  if (!options.forceRefresh) {
    const cached = localeBundlePromises.get(locale)
    if (cached) return cached
    const sessionBundle = readSessionLocaleBundle(locale)
    if (sessionBundle) {
      const resolved = Promise.resolve(sessionBundle)
      localeBundlePromises.set(locale, resolved)
      return resolved
    }
  }
  const request = fetchJson<unknown>(`/data/presentation/locales/${locale}.json`, options)
    .then((payload) => readPresentationLocaleBundle(payload, locale))
    .then((bundle) => {
      if (bundle) writeSessionLocaleBundle(locale, bundle)
      return bundle
    })
    .catch(() => null)
  localeBundlePromises.set(locale, request)
  return request
}

function loadPresentationHomeBundle(options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || homeBundlePromise === null) {
    homeBundlePromise = fetchJson<PresentationHomeBundle>(PRESENTATION_HOME_PATH, options)
  }
  return homeBundlePromise
}

function loadPresentationCatalogManifest(options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || catalogManifestPromise === null) {
    catalogManifestPromise = fetchJson<PresentationCatalogManifest>(PRESENTATION_CATALOG_PATH, options)
  }
  return catalogManifestPromise
}

function loadPresentationDetailsIndex(options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || detailsIndexPromise === null) {
    detailsIndexPromise = fetchJson<PresentationDetailsIndex>(PRESENTATION_DETAILS_PATH, options)
  }
  return detailsIndexPromise
}

function loadPresentationSearchBundle(options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || searchBundlePromise === null) {
    searchBundlePromise = fetchJson<PresentationSearchBundle>(PRESENTATION_SEARCH_PATH, options)
  }
  return searchBundlePromise
}

function loadPresentationCatalogPage(publicPath: string, options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || !catalogPagePromises.has(publicPath)) {
    catalogPagePromises.set(publicPath, fetchJson<PresentationCatalogPage>(publicPath, options))
  }
  return catalogPagePromises.get(publicPath)!
}

function loadPresentationDetailsPage(publicPath: string, options: { forceRefresh?: boolean } = {}) {
  if (options.forceRefresh || !detailsPagePromises.has(publicPath)) {
    detailsPagePromises.set(publicPath, fetchJson<PresentationDetailsPage>(publicPath, options))
  }
  return detailsPagePromises.get(publicPath)!
}

function finiteNumber(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value)
}

function clampProbability(value: number) {
  return Math.max(0, Math.min(1, value))
}

function presentationEventToCatalog(record: PresentationEventRecord): PredictionEventCatalog {
  const category = record.displayCategory ?? record.resolvedCategory ?? record.category
  return {
    id: record.id,
    title: record.title,
    originalTitle: record.originalTitle,
    category,
    rawCategory: record.rawCategory ?? record.category,
    resolvedCategory: record.resolvedCategory ?? category,
    displayCategory: category,
    eventType: record.eventType as PredictionEventCatalog['eventType'],
    sources: record.sources,
    markets: record.markets.map((market) => ({
      id: market.id,
      source: market.source,
      title: market.title ?? undefined,
      originalTitle: market.originalTitle ?? undefined,
      probability: market.probability ?? undefined,
      sourceUrl: market.sourceUrl ?? undefined,
      updatedAt: market.updatedAt ?? undefined,
      endTime: market.endTime ?? undefined,
      contentHash: record.contentHash ?? `presentation:${record.id}`,
      dynamicHash: record.dynamicHash ?? `presentation:${record.id}`,
      relationshipKind: record.groupingMode ?? 'native',
      eventKey: record.id,
      nativeEventId: record.nativeEventId ?? undefined,
      nativeSeriesId: record.nativeSeriesId ?? undefined,
      nativeEventTitle: record.nativeEventTitle ?? undefined,
      nativeSeriesTitle: record.nativeSeriesTitle ?? undefined,
    } as unknown as PredictionEventCatalog['markets'][number])),
    outcomes: record.outcomes.map((outcome) => ({
      id: outcome.id,
      label: outcome.label,
      probability: outcome.probability ?? undefined,
      probabilities: outcome.probabilities.map((probability) => ({
        source: probability.source,
        marketId: probability.marketId,
        probability: probability.probability ?? undefined,
        updatedAt: probability.updatedAt ?? undefined,
      })),
      marketIds: outcome.marketIds,
    })),
    deadline: record.deadline ?? undefined,
    status: record.status as PredictionEventCatalog['status'],
    heat: record.heat ?? 0,
    change24h: record.change24h ?? undefined,
    sourceCount: record.sourceCount ?? record.sources.length,
    sourceUrls: record.sourceUrls ?? [],
    updatedAt: record.updatedAt ?? undefined,
    translationStatus: (record.translationStatus ?? 'success') as PredictionEventCatalog['translationStatus'],
    classificationStatus: (record.classificationStatus ?? 'success') as PredictionEventCatalog['classificationStatus'],
    matchConfidence: (record.matchConfidence ?? 'high') as PredictionEventCatalog['matchConfidence'],
    consensusStatus: (record.consensusStatus ?? 'insufficient-data') as PredictionEventCatalog['consensusStatus'],
    valueSignals: {
      score: 0,
      reasons: [],
      dimensions: {
        heat: 0,
        change: 0,
        deadline: 0,
        sourceCount: 0,
        divergence: 0,
        freshness: 0,
        quality: 0,
        categoryDiversity: 0,
      },
      weightsVersion: 'presentation-bundle-v1',
    },
    displayEligible: record.displayEligible,
    headlineEligible: record.headlineEligible,
    activity: record.activity ?? undefined,
    groupingMode: record.groupingMode ?? 'native',
    nativeEventId: record.nativeEventId ?? undefined,
    nativeSeriesId: record.nativeSeriesId ?? undefined,
    nativeEventTitle: record.nativeEventTitle ?? undefined,
    nativeSeriesTitle: record.nativeSeriesTitle ?? undefined,
    contentHash: record.contentHash ?? `presentation:${record.id}`,
    dynamicHash: record.dynamicHash ?? `presentation:${record.id}`,
    matchingSignature: record.matchingSignature ?? `presentation:${record.id}`,
  }
}

function presentationEventsToCatalog(records: PresentationEventRecord[]) {
  return records.map(presentationEventToCatalog)
}

function primaryProbability(event: PredictionEventCatalog) {
  const probability = event.outcomes[0]?.probability ?? event.representative?.probability
  return finiteNumber(probability) ? clampProbability(probability) : undefined
}

function sourceProbability(event: PredictionEventCatalog, source: string) {
  const values = event.outcomes.flatMap((outcome) => outcome.probabilities)
    .filter((item) => item.source.toLowerCase() === source.toLowerCase())
  if (values.length === 0) return undefined
  return values[0].probability
}

function sourceUrl(event: PredictionEventCatalog, source: string) {
  const market = event.markets.find((item) => item.source.toLowerCase() === source.toLowerCase())
  return market?.sourceUrl
}

function outcomesFor(
  event: PredictionEventCatalog,
  displayTitle: string,
  resolvedLabels?: Map<string, string>,
): PresentationOutcome[] {
  return event.outcomes.map((outcome) => ({
    id: outcome.id,
    label: outcome.label,
    displayOutcomeLabel: resolvedLabels?.get(outcome.id) ?? localDisplayOutcomeLabel(event, outcome, displayTitle),
    probability: outcome.probability,
    sourceProbabilities: outcome.probabilities.map((item) => ({
      source: item.source,
      probability: item.probability,
      updatedAt: item.updatedAt,
    })),
  }))
}

function validChangeEvidence(entry?: PresentationRankingEntry) {
  const evidence = entry?.probabilityEvidence?.change
  if (!evidence || evidence.supported !== true || !finiteNumber(evidence.delta)) return undefined
  return {
    delta: evidence.delta,
    current: finiteNumber(evidence.currentProbability) ? evidence.currentProbability : undefined,
    previous: finiteNumber(evidence.previousProbability24h) ? evidence.previousProbability24h : undefined,
  }
}

function eventChange(event: PredictionEventCatalog, entry?: PresentationRankingEntry) {
  const evidence = validChangeEvidence(entry)
  const change = evidence?.delta ?? event.change24h
  if (!finiteNumber(change)) return { change: undefined, previous: undefined }
  const current = primaryProbability(event)
  const previous = evidence?.previous ?? (current === undefined ? undefined : clampProbability(current - change))
  return { change, previous }
}

function changeDirection(change: number | undefined): ChangeDirection {
  if (!finiteNumber(change)) return 'unknown'
  if (change === 0) return 'flat'
  return change > 0 ? 'up' : 'down'
}

function changeLabel(change: number | undefined, locale: PresentationLocale) {
  if (!finiteNumber(change)) return tForLocale(locale, 'common.changeUnknown')
  if (change === 0) return tForLocale(locale, 'common.changeFlat')
  const rawPoints = formatPercentagePoints(change)
  const points = rawPoints === '不到1' ? tForLocale(locale, 'common.lessThanOnePoint') : rawPoints
  return tForLocale(locale, change > 0 ? 'common.changeUp' : 'common.changeDown', { points })
}

function formatRemainingDays(days: number, locale: PresentationLocale) {
  if (days < 1) return tForLocale(locale, 'common.lessThanOneDay')
  if (days < 2) return tForLocale(locale, 'common.aboutOneDay')
  return tForLocale(locale, 'common.aboutDays', { days: Math.ceil(days) })
}

function triggerReason(entry: PresentationRankingEntry | undefined, event: PredictionEventCatalog, locale: PresentationLocale) {
  const evidence = entry?.todayTriggerEvidence ?? []
  const supported = evidence.filter((item) => item.evidence?.supported === true)
  const priority = ['probability-change', 'threshold-crossing', 'deadline', 'new-event', 'new-source', 'market-heat', 'freshness', 'cross-source-state']
  const selected = priority.map((kind) => supported.find((item) => item.kind === kind)).find(Boolean)
  if (selected?.kind === 'probability-change') {
    const delta = selected.evidence?.delta
    return finiteNumber(delta) ? changeLabel(delta, locale) : selected.reason
  }
  if (selected?.kind === 'threshold-crossing') {
    const level = selected.evidence?.level
    return finiteNumber(level)
      ? tForLocale(locale, 'common.probabilityCrossed', { value: Math.round(level * 100) })
      : selected.reason
  }
  if (selected?.kind === 'deadline') {
    const days = selected.evidence?.daysRemaining
    return finiteNumber(days)
      ? tForLocale(locale, 'common.untilResolution', { time: formatRemainingDays(days, locale) })
      : selected.reason
  }
  if (selected?.kind === 'new-event') return tForLocale(locale, 'common.todayAdded')
  if (selected?.kind === 'new-source') return tForLocale(locale, 'common.todayNewSource')
  if (selected?.kind === 'market-heat') return tForLocale(locale, 'common.marketHeat')
  if (selected?.kind === 'freshness') return tForLocale(locale, 'common.marketUpdated')
  if (selected?.kind === 'cross-source-state') return tForLocale(locale, 'common.crossSource')
  if (entry?.todayTriggerReasons?.length) return entry.todayTriggerReasons[0]
  if (finiteNumber(event.change24h) && event.change24h !== 0) return changeLabel(event.change24h, locale)
  return undefined
}

function normalizedText(event: PredictionEventCatalog) {
  return [event.title, event.originalTitle, event.nativeEventTitle, event.nativeSeriesTitle]
    .filter(Boolean)
    .join(' ')
    .toLowerCase()
}

function importanceReason(event: PredictionEventCatalog, entry: PresentationRankingEntry | undefined, locale: PresentationLocale) {
  const text = normalizedText(event)
  const topics = new Set(entry?.topics ?? [])
  const labels = entry?.topicLabels ?? []

  if (topics.has('macro') || /inflation|cpi|gdp|interest rate|federal reserve|fed funds|通胀|消费价格|国内生产总值|利率|美联储/u.test(text)) {
    if (/inflation|cpi|通胀|消费价格/u.test(text)) return tForLocale(locale, 'importance.inflation')
    if (/gdp|经济增长|国内生产总值/u.test(text)) return tForLocale(locale, 'importance.growth')
    return tForLocale(locale, 'importance.rates')
  }
  if (topics.has('chinaDirect') || /china|taiwan|hong kong|中国|台湾|香港/u.test(text)) {
    return tForLocale(locale, 'importance.chinaDirect')
  }
  if (topics.has('geopolitics') || /war|military|invasion|iran|israel|russia|ukraine|战争|军事|入侵|伊朗|以色列|俄罗斯|乌克兰/u.test(text)) {
    return tForLocale(locale, 'importance.geopolitics')
  }
  if (topics.has('aiTechnology') && topics.has('technologyCompany')) {
    return tForLocale(locale, 'importance.aiCompany')
  }
  if (topics.has('aiTechnology') || /openai|anthropic|spacex|artificial intelligence|ai model|人工智能|模型/u.test(text)) {
    return tForLocale(locale, 'importance.ai')
  }
  if (topics.has('ipo') || topics.has('majorMarket') || /ipo|market cap|市值|上市/u.test(text)) {
    return tForLocale(locale, 'importance.market')
  }
  if (topics.has('cryptoTrend') || /bitcoin|ethereum|crypto|blockchain|比特币|以太坊|加密资产/u.test(text)) {
    return tForLocale(locale, 'importance.crypto')
  }
  if (topics.has('majorSports') || event.category === 'sports') {
    return tForLocale(locale, 'importance.sports')
  }
  if (event.category === 'entertainment') {
    return tForLocale(locale, 'importance.entertainment')
  }
  if (labels.length > 0) return tForLocale(locale, 'importance.topic', { topic: labels[0] })
  return tForLocale(locale, 'importance.category', { category: categoryLabel(event.category, locale) })
}

function consensusState(event: PredictionEventCatalog, locale: PresentationLocale): { state: ConsensusState; label?: string } {
  if (event.sourceCount < 2 || event.sources.length < 2) return { state: 'single-source' }
  if (event.consensusStatus === 'consensus') return { state: 'consensus', label: tForLocale(locale, 'common.marketClose') }
  if (event.consensusStatus === 'divergence') return { state: 'divergence', label: tForLocale(locale, 'common.marketDivergence') }
  return { state: 'insufficient-data' }
}

function sourceViews(event: PredictionEventCatalog): PresentationSource[] {
  return event.sources.map((source) => ({
    name: source,
    mode: 'synced',
    probability: sourceProbability(event, source),
    sourceUrl: sourceUrl(event, source),
    updatedAt: event.markets.find((market) => market.source.toLowerCase() === source.toLowerCase())?.updatedAt,
  }))
}

function buildViewModel(
  event: PredictionEventCatalog,
  entry: PresentationRankingEntry | undefined,
  rankingType: PresentationRankingType,
  relatedEventCount = Math.max(0, (entry?.storyClusterSize ?? 1) - 1),
  resolvedDisplayTitle?: string,
  resolvedOutcomeLabels?: Map<string, string>,
  locale: PresentationLocale = 'zh-CN',
): PredictionViewModel {
  const probability = primaryProbability(event)
  const { change, previous } = eventChange(event, entry)
  const consensus = consensusState(event, locale)
  const binary = event.eventType === 'binary'
  const outcomeLabel = event.outcomes[0]?.label
  const displayTitle = resolvedDisplayTitle || entry?.displayTitle?.trim() || event.title || event.originalTitle || tForLocale(locale, 'common.untitledPrediction')
  const structure = inferOutcomeStructure(event)
  return {
    eventId: event.id,
    displayTitle,
    originalTitle: event.originalTitle,
    category: event.displayCategory ?? event.resolvedCategory ?? event.category,
    probability,
    probabilityLabel: probability === undefined ? tForLocale(locale, 'card.noCurrentProbability') : tForLocale(locale, 'card.currentProbabilityLabel', { value: formatProbability(probability) }),
    probabilityInterpretation: probabilityExplanation(probability, locale),
    previousProbability: previous,
    change24h: change,
    changeDirection: changeDirection(change),
    changeLabel: changeLabel(change, locale),
    todayReason: rankingType === 'watch'
      ? tForLocale(locale, 'common.todayWatch')
      : triggerReason(entry, event, locale),
    importanceReason: importanceReason(event, entry, locale),
    sourceSummary: event.sources.join(' + '),
    sourceCount: event.sourceCount,
    sources: sourceViews(event),
    consensusState: consensus.state,
    consensusLabel: consensus.label,
    deadline: event.deadline,
    deadlineLabel: event.deadline
      ? tForLocale(locale, isDeadlinePassed(event.deadline) ? 'common.endedAt' : 'common.deadline', { time: formatEndTime(event.deadline, locale) })
      : tForLocale(locale, 'common.deadlineUnknown'),
    temporalStatus: event.deadline === undefined ? 'unknown' : isDeadlinePassed(event.deadline) ? 'ended' : 'open',
    storyId: entry?.storyKey ?? event.nativeEventId ?? event.nativeSeriesId,
    relatedEventCount,
    rankingType,
    displayEligible: !isDeadlinePassed(event.deadline) && (entry?.displayEligible ?? event.displayEligible === true),
    eventType: event.eventType,
    isBinary: binary,
    outcomes: outcomesFor(event, displayTitle, resolvedOutcomeLabels),
    outcomeStructure: structure,
    outcomeStructureLabel: structure === 'mutually-exclusive'
      ? tForLocale(locale, 'card.outcomeMutuallyExclusive')
      : structure === 'independent-conditions'
        ? tForLocale(locale, 'card.outcomeIndependent')
        : locale === 'en' ? tForLocale(locale, 'card.outcomeDistribution') : outcomeStructureLabel(structure),
    outcomeStructureNote: structure === 'independent-conditions'
      ? tForLocale(locale, 'card.outcomeIndependentNote')
      : locale === 'en' ? undefined : outcomeStructureNote(structure),
    displaySemanticKey: buildDisplaySemanticKey(event),
    sourceUrls: event.sourceUrls,
    updatedAt: event.updatedAt,
    ...(binary || !outcomeLabel
      ? {}
      : { probabilityLabel: probability === undefined ? tForLocale(locale, 'card.noVerifiableOutcomeProbability') : tForLocale(locale, 'card.highestOutcomeProbability', { value: formatProbability(probability) }) }),
  }
}

function rankingMap(payload: PresentationRankingPayload) {
  return new Map(Object.entries(payload.metadata))
}

function uniqueIds(entries: PresentationRankingEntry[]) {
  return [...new Set(entries.map((entry) => entry.id))]
}

function enabledSourceNames() {
  if (typeof window === 'undefined') return new Set(['polymarket', 'kalshi'])
  return new Set(loadDataSourceConfigs(defaultDataSourceConfigs)
    .filter((config) => config.enabled)
    .map((config) => config.id))
}

function filterEnabled(events: PredictionEventCatalog[]) {
  const enabled = enabledSourceNames()
  return events.filter((event) => event.sources.some((source) => enabled.has(source.toLowerCase())))
}

function sourceStatusFromEvents(
  events: PredictionEventCatalog[],
  freshness: CurrentFreshness,
  sourceResults?: EventSourceResult[],
  locale: PresentationLocale = 'zh-CN',
): PresentationSourceStatus[] {
  const resultById = new Map(sourceResults?.map((source) => [source.id, source]))
  return (['polymarket', 'kalshi'] as const).map((id) => {
    const eventCount = events.filter((event) => event.sources.some((source) => source.toLowerCase() === id)).length
    const result = resultById.get(id)
    const disabled = result?.status === 'disabled'
    const sourceName = id === 'polymarket' ? 'Polymarket' : 'Kalshi'
    const source = freshness.sources[sourceName]
    const status = disabled ? 'disabled' as const : sourceFreshnessStatus(source)
    const lastSuccessful = source.lastSuccessfulSyncAt
      ? tForLocale(locale, 'common.lastSuccessfulSync', { time: formatUpdatedAt(source.lastSuccessfulSyncAt, false, locale) })
      : tForLocale(locale, 'common.noSuccessfulSync')
    const detail = disabled
      ? tForLocale(locale, 'common.hidden')
      : status === 'degraded'
        ? `${tForLocale(locale, 'common.syncFailedUsingLast')} · ${lastSuccessful}`
        : status === 'no-data'
          ? tForLocale(locale, 'common.noData')
          : `${sourceFreshnessStatusLabel(status, locale)} · ${lastSuccessful}${eventCount > 0 ? ` · ${tForLocale(locale, 'common.currentlyShown', { count: eventCount })}` : ''}`
    return {
      name: sourceName,
      mode: 'synced',
      status,
      detail,
      lastSuccessfulSyncAt: source.lastSuccessfulSyncAt,
      error: source.error,
    }
  })
}

export interface PresentationHomeFeed {
  kind: 'home'
  today: PredictionViewModel[]
  changing: PredictionViewModel[]
  watch: PredictionViewModel[]
  comparisons: PredictionViewModel[]
  sources: PresentationSourceStatus[]
  freshness: CurrentFreshness
  fetchedAt: string
  rankingGeneratedAt: string
  formalCatalogState: PresentationRankingPayload['formalCatalogState']
}

export interface PresentationCatalogFeed {
  kind: 'catalog'
  results: PredictionViewModel[]
  sources: PresentationSourceStatus[]
  freshness: CurrentFreshness
  fetchedAt: string
  hasMore: boolean
  nextPage: number
  total: number
  query?: string
  category?: PredictionCategory | null
}

async function resolveDisplayTitles(
  events: PredictionEventCatalog[],
  metadata: Map<string, PresentationRankingEntry>,
  locale: PresentationLocale,
  localeBundle: PresentationLocaleBundle | null,
) {
  const baseTitles = new Map(events.map((event) => {
    const bundleTitle = localeBundle?.events[event.id]?.title?.text?.trim()
    const originalTitle = event.originalTitle ?? event.title
    const fallback = locale === 'en'
      ? originalTitle
      : metadata.get(event.id)?.displayTitle
        ?? (metadata.has(event.id) ? undefined : event.translationStatus === 'success' ? event.title : undefined)
        ?? originalTitle
    const title = bundleTitle || fallback
    return [event.id, locale === 'en' ? title : presentationTitleForEvent(event, title)] as const
  }))
  return locale === 'en' ? baseTitles : ensureUniquePresentationTitles(events, baseTitles)
}

async function resolvePresentationContent(
  events: PredictionEventCatalog[],
  metadata: Map<string, PresentationRankingEntry>,
  locale: PresentationLocale,
  localeBundle: PresentationLocaleBundle | null,
) {
  const titles = await resolveDisplayTitles(events, metadata, locale, localeBundle)
  const outcomeEntries = events.flatMap((event) => event.outcomes.map((outcome) => {
    const bundleOutcome = localeBundle?.events[event.id]?.outcomes?.[outcome.id]?.text?.trim()
    const fallback = locale === 'en'
      ? outcomeSourceTitle(event, outcome)
      : localDisplayOutcomeLabel(event, outcome, titles.get(event.id))
    return [event.id, outcome.id, bundleOutcome || fallback] as const
  }))
  const outcomes = new Map<string, Map<string, string>>()
  for (const [eventId, outcomeId, label] of outcomeEntries) {
    const labels = outcomes.get(eventId) ?? new Map<string, string>()
    labels.set(outcomeId, label)
    outcomes.set(eventId, labels)
  }
  return { titles, outcomes }
}

function buildHomeFeed(
  ranking: PresentationRankingPayload,
  events: PredictionEventCatalog[],
  freshness: CurrentFreshness,
  locale: PresentationLocale,
  localeBundle: PresentationLocaleBundle | null,
): Promise<PresentationHomeFeed> {
  const eventsById = new Map(events.map((event) => [event.id, event]))
  const metadata = rankingMap(ranking)
  return resolvePresentationContent(events, metadata, locale, localeBundle).then((presentationContent) => {
    const build = (entries: PresentationRankingEntry[], type: PresentationRankingType) => entries
      .map((entry) => {
        const event = eventsById.get(entry.id)
        return event && !isDeadlinePassed(event.deadline) ? buildViewModel(
          event,
          metadata.get(entry.id),
          type,
          undefined,
          presentationContent.titles.get(event.id),
          presentationContent.outcomes.get(event.id),
          locale,
        ) : undefined
      })
      .filter((item): item is PredictionViewModel => Boolean(item))
    const comparisons = ranking.formalComparisons
      .filter(({ consensusStatus }) => consensusStatus === 'consensus' || consensusStatus === 'divergence')
      .map(({ id }) => {
        const event = eventsById.get(id)
        return event && !isDeadlinePassed(event.deadline) ? buildViewModel(
          event,
          metadata.get(id),
          'related',
          undefined,
          presentationContent.titles.get(event.id),
          presentationContent.outcomes.get(event.id),
          locale,
        ) : undefined
      })
      .filter((item): item is PredictionViewModel => Boolean(item))
    return {
      kind: 'home',
      today: build(ranking.todayTop10, 'today'),
      changing: build(ranking.changing, 'changing'),
      watch: build(ranking.watchTop10, 'watch'),
      comparisons,
      sources: sourceStatusFromEvents(events, freshness, undefined, locale),
      freshness,
      fetchedAt: freshness.dataset.presentationGeneratedAt
        ?? freshness.dataset.eventGeneratedAt
        ?? freshness.generatedAt,
      rankingGeneratedAt: ranking.generatedAt,
      formalCatalogState: ranking.formalCatalogState,
    }
  })
}

async function getLegacyPresentationHomeFeed(locale: PresentationLocale, options: { forceRefresh?: boolean } = {}): Promise<PresentationHomeFeed> {
  const [ranking, freshness, localeBundle] = await Promise.all([
    loadPresentationRanking(options),
    loadDatasetFreshness(options),
    loadPresentationLocaleBundle(locale, options),
  ])
  const rankingEntries = [
    ...ranking.todayTop10,
    ...ranking.changing,
    ...ranking.watchTop10,
    ...ranking.formalComparisons.map(({ id }) => ranking.metadata[id] ?? { id }),
  ]
  const events = filterEnabled(await loadEventsByIds(uniqueIds(rankingEntries), options))
  return buildHomeFeed(ranking, events, freshness, locale, localeBundle)
}

async function getBundlePresentationHomeFeed(locale: PresentationLocale, options: { forceRefresh?: boolean } = {}): Promise<PresentationHomeFeed> {
  try {
    const [bundle, freshness, localeBundle] = await Promise.all([
      loadPresentationHomeBundle(options),
      loadDatasetFreshness(options),
      loadPresentationLocaleBundle(locale, options),
    ])
    return buildHomeFeed(bundle, filterEnabled(presentationEventsToCatalog(bundle.events)), freshness, locale, localeBundle)
  } catch (error) {
    if (!isNotFound(error)) throw error
    return getLegacyPresentationHomeFeed(locale, options)
  }
}

export function getPresentationHomeFeed(options: { forceRefresh?: boolean; locale?: PresentationLocale } = {}): Promise<PresentationHomeFeed> {
  const locale = options.locale ?? 'zh-CN'
  const key = `home:${locale}`
  if (options.forceRefresh) {
    const request = getBundlePresentationHomeFeed(locale, options)
    homeFeedPromises.set(key, request)
    request.catch(() => {
      if (homeFeedPromises.get(key) === request) homeFeedPromises.delete(key)
    })
    return request
  }
  const cached = homeFeedPromises.get(key)
  if (cached) return cached
  const request = getBundlePresentationHomeFeed(locale, options)
  homeFeedPromises.set(key, request)
  request.catch(() => {
    if (homeFeedPromises.get(key) === request) homeFeedPromises.delete(key)
  })
  return request
}

function catalogSortValue(event: PredictionEventCatalog, metadata: PresentationRankingEntry | undefined) {
  const informationValue = metadata?.todayInformationValue
    ?? metadata?.publicInformationValue
    ?? event.valueSignals?.score
    ?? 0
  const todaySignal = metadata?.todaySignal ?? 0
  return informationValue * 1000
    + todaySignal * 10
    + Math.abs(event.change24h ?? 0) * 100
    + (event.heat ?? 0)
}

async function getLegacyPresentationCatalogFeed(locale: PresentationLocale, options: {
  forceRefresh?: boolean
  category?: PredictionCategory | null
  query?: string
  page?: number
} = {}): Promise<PresentationCatalogFeed> {
  const page = options.page ?? 0
  const requestedStart = page * PRESENTATION_PAGE_SIZE
  const [feed, ranking, freshness, localeBundle] = await Promise.all([
    getEventCatalogFeed({ ...options, page: 0 }),
    loadPresentationRanking(options),
    loadDatasetFreshness(options),
    loadPresentationLocaleBundle(locale, options),
  ])
  const metadata = rankingMap(ranking)
  const rankingType: PresentationRankingType = options.query ? 'search' : 'category'
  const orderedEvents = filterEnabled(feed.events)
    .slice()
    .sort((left, right) => catalogSortValue(right, metadata.get(right.id)) - catalogSortValue(left, metadata.get(left.id)))
  const pageEvents = orderedEvents.slice(requestedStart, requestedStart + PRESENTATION_PAGE_SIZE)
  const presentationContent = await resolvePresentationContent(pageEvents, metadata, locale, localeBundle)
  const results = pageEvents
    .map((event) => buildViewModel(
      event,
      metadata.get(event.id),
      rankingType,
      undefined,
      presentationContent.titles.get(event.id),
      presentationContent.outcomes.get(event.id),
      locale,
    ))
  const hasMore = requestedStart + PRESENTATION_PAGE_SIZE < orderedEvents.length
  return {
    kind: 'catalog',
    results,
    sources: sourceStatusFromEvents(pageEvents, freshness, feed.sources, locale),
    freshness,
    fetchedAt: freshness.dataset.presentationGeneratedAt
      ?? freshness.dataset.eventGeneratedAt
      ?? freshness.generatedAt,
    hasMore,
    nextPage: hasMore ? page + 1 : page,
    total: results.length,
    query: options.query,
    category: options.category,
  }
}

function buildPresentationCatalogResult(
  pageEvents: PredictionEventCatalog[],
  ranking: PresentationRankingPayload,
  freshness: CurrentFreshness,
  locale: PresentationLocale,
  localeBundle: PresentationLocaleBundle | null,
  options: { category?: PredictionCategory | null; query?: string },
  total: number,
  page: number,
): Promise<PresentationCatalogFeed> {
  const metadata = rankingMap(ranking)
  const rankingType: PresentationRankingType = options.query ? 'search' : 'category'
  const visiblePageEvents = pageEvents.filter((event) => !isDeadlinePassed(event.deadline))
  return resolvePresentationContent(visiblePageEvents, metadata, locale, localeBundle).then((presentationContent) => {
    const results = visiblePageEvents.map((event) => buildViewModel(
      event,
      metadata.get(event.id),
      rankingType,
      undefined,
      presentationContent.titles.get(event.id),
      presentationContent.outcomes.get(event.id),
      locale,
    ))
    const hasMore = (page + 1) * PRESENTATION_PAGE_SIZE < total
    return {
      kind: 'catalog',
      results,
      sources: sourceStatusFromEvents(visiblePageEvents, freshness, undefined, locale),
      freshness,
      fetchedAt: freshness.dataset.presentationGeneratedAt
        ?? freshness.dataset.eventGeneratedAt
        ?? freshness.generatedAt,
      hasMore,
      nextPage: hasMore ? page + 1 : page,
      total,
      query: options.query,
      category: options.category,
    }
  })
}

function searchMatches(event: PredictionEventCatalog, query: string) {
  return normalizedText(event).includes(query.trim().toLowerCase())
}

async function getBundlePresentationCatalogFeed(locale: PresentationLocale, options: {
  forceRefresh?: boolean
  category?: PredictionCategory | null
  query?: string
  page?: number
} = {}): Promise<PresentationCatalogFeed> {
  try {
    const page = options.page ?? 0
    const requestedStart = page * PRESENTATION_PAGE_SIZE
    const [ranking, freshness, localeBundle] = await Promise.all([
      loadPresentationRanking(options),
      loadDatasetFreshness(options),
      loadPresentationLocaleBundle(locale, options),
    ])
    const metadata = rankingMap(ranking)

    if (options.query?.trim()) {
      const searchBundle = await loadPresentationSearchBundle(options)
      const orderedEvents = filterEnabled(presentationEventsToCatalog(searchBundle.events))
        .filter((event) => !isDeadlinePassed(event.deadline))
        .filter((event) => searchMatches(event, options.query!))
        .sort((left, right) => catalogSortValue(right, metadata.get(right.id)) - catalogSortValue(left, metadata.get(left.id)))
      return buildPresentationCatalogResult(
        orderedEvents.slice(requestedStart, requestedStart + PRESENTATION_PAGE_SIZE),
        ranking,
        freshness,
        locale,
        localeBundle,
        options,
        orderedEvents.length,
        page,
      )
    }

    if (options.category) {
      const manifest = await loadPresentationCatalogManifest(options)
      const category = manifest.categories[options.category]
      if (!category) return buildPresentationCatalogResult([], ranking, freshness, locale, localeBundle, options, 0, page)
      const pagePath = category.pages[page]
      if (!pagePath) return buildPresentationCatalogResult([], ranking, freshness, locale, localeBundle, options, category.count, page)
      const catalogPage = await loadPresentationCatalogPage(pagePath, options)
      return buildPresentationCatalogResult(
        filterEnabled(presentationEventsToCatalog(catalogPage.events)).filter((event) => !isDeadlinePassed(event.deadline)),
        ranking,
        freshness,
        locale,
        localeBundle,
        options,
        category.count,
        page,
      )
    }

    const searchBundle = await loadPresentationSearchBundle(options)
    const orderedEvents = filterEnabled(presentationEventsToCatalog(searchBundle.events))
      .filter((event) => !isDeadlinePassed(event.deadline))
      .sort((left, right) => catalogSortValue(right, metadata.get(right.id)) - catalogSortValue(left, metadata.get(left.id)))
    return buildPresentationCatalogResult(
      orderedEvents.slice(requestedStart, requestedStart + PRESENTATION_PAGE_SIZE),
      ranking,
      freshness,
      locale,
      localeBundle,
      options,
      orderedEvents.length,
      page,
    )
  } catch (error) {
    if (!isNotFound(error)) throw error
    return getLegacyPresentationCatalogFeed(locale, options)
  }
}

export function getPresentationCatalogFeed(options: {
  forceRefresh?: boolean
  category?: PredictionCategory | null
  query?: string
  page?: number
  locale?: PresentationLocale
} = {}): Promise<PresentationCatalogFeed> {
  const locale = options.locale ?? 'zh-CN'
  const key = JSON.stringify({ locale, category: options.category ?? null, query: options.query ?? '', page: options.page ?? 0 })
  if (options.forceRefresh) {
    const request = getBundlePresentationCatalogFeed(locale, options)
    catalogFeedPromises.set(key, request)
    request.catch(() => {
      if (catalogFeedPromises.get(key) === request) catalogFeedPromises.delete(key)
    })
    return request
  }
  const cached = catalogFeedPromises.get(key)
  if (cached) return cached
  const request = getBundlePresentationCatalogFeed(locale, options)
  catalogFeedPromises.set(key, request)
  request.catch(() => {
    if (catalogFeedPromises.get(key) === request) catalogFeedPromises.delete(key)
  })
  return request
}

export interface PresentationDetail {
  prediction: PredictionViewModel
  related: PredictionViewModel[]
}

async function getLegacyPresentationDetail(eventId: string, locale: PresentationLocale, options: { forceRefresh?: boolean } = {}): Promise<PresentationDetail | null> {
  const [ranking, localeBundle] = await Promise.all([
    loadPresentationRanking(options),
    loadPresentationLocaleBundle(locale, options),
  ])
  const event = (await loadEventsByIds([eventId], options))[0]
  if (!event) return null
  const metadata = rankingMap(ranking)
  const story = ranking.storyClusters.find((cluster) => cluster.ids.includes(eventId))
  const relatedIds = story?.ids.filter((id) => id !== eventId).slice(0, 5) ?? []
  const relatedEvents = await loadEventsByIds(relatedIds, options)
  const presentationContent = await resolvePresentationContent([event, ...relatedEvents], metadata, locale, localeBundle)
  const related = relatedEvents.map((relatedEvent) => buildViewModel(
    relatedEvent,
    metadata.get(relatedEvent.id),
    'related',
    undefined,
    presentationContent.titles.get(relatedEvent.id),
    presentationContent.outcomes.get(relatedEvent.id),
    locale,
  ))
  return {
    prediction: buildViewModel(
      event,
      metadata.get(eventId),
      metadata.has(eventId) ? (metadata.get(eventId)?.todayRank ? 'today' : metadata.get(eventId)?.watchRank ? 'watch' : 'related') : 'related',
      related.length,
      presentationContent.titles.get(event.id),
      presentationContent.outcomes.get(event.id),
      locale,
    ),
    related,
  }
}

async function getBundlePresentationDetail(eventId: string, locale: PresentationLocale, options: { forceRefresh?: boolean } = {}): Promise<PresentationDetail | null> {
  try {
    const [ranking, localeBundle, detailsIndex] = await Promise.all([
      loadPresentationRanking(options),
      loadPresentationLocaleBundle(locale, options),
      loadPresentationDetailsIndex(options),
    ])
    const eventPagePath = detailsIndex.events[eventId]
    if (!eventPagePath) return null
    const eventPage = await loadPresentationDetailsPage(eventPagePath, options)
    const primaryEvents = presentationEventsToCatalog(eventPage.events)
    const eventsById = new Map(primaryEvents.map((event) => [event.id, event]))
    const event = eventsById.get(eventId)
    if (!event) return null
    const metadata = rankingMap(ranking)
    const story = ranking.storyClusters.find((cluster) => cluster.ids.includes(eventId))
    const relatedIds = story?.ids.filter((id) => id !== eventId).slice(0, 5) ?? []
    const relatedPagePaths = [...new Set(relatedIds.map((id) => detailsIndex.events[id]).filter(Boolean))]
    const relatedPages = await Promise.all(relatedPagePaths.map((publicPath) => loadPresentationDetailsPage(publicPath, options)))
    for (const page of relatedPages) {
      for (const relatedEvent of presentationEventsToCatalog(page.events)) eventsById.set(relatedEvent.id, relatedEvent)
    }
    const relatedEvents = relatedIds.map((id) => eventsById.get(id)).filter((item): item is PredictionEventCatalog => Boolean(item))
    const presentationContent = await resolvePresentationContent([event, ...relatedEvents], metadata, locale, localeBundle)
    const related = relatedEvents.map((relatedEvent) => buildViewModel(
      relatedEvent,
      metadata.get(relatedEvent.id),
      'related',
      undefined,
      presentationContent.titles.get(relatedEvent.id),
      presentationContent.outcomes.get(relatedEvent.id),
      locale,
    ))
    return {
      prediction: buildViewModel(
        event,
        metadata.get(eventId),
        metadata.has(eventId) ? (metadata.get(eventId)?.todayRank ? 'today' : metadata.get(eventId)?.watchRank ? 'watch' : 'related') : 'related',
        related.length,
        presentationContent.titles.get(event.id),
        presentationContent.outcomes.get(event.id),
        locale,
      ),
      related,
    }
  } catch (error) {
    if (!isNotFound(error)) throw error
    return getLegacyPresentationDetail(eventId, locale, options)
  }
}

export function getPresentationDetail(eventId: string, options: { forceRefresh?: boolean; locale?: PresentationLocale } = {}): Promise<PresentationDetail | null> {
  const locale = options.locale ?? 'zh-CN'
  const key = `${locale}|${eventId}`
  if (options.forceRefresh) {
    const request = getBundlePresentationDetail(eventId, locale, options)
    detailPromises.set(key, request)
    request.catch(() => {
      if (detailPromises.get(key) === request) detailPromises.delete(key)
    })
    return request
  }
  const cached = detailPromises.get(key)
  if (cached) return cached
  const request = getBundlePresentationDetail(eventId, locale, options)
  detailPromises.set(key, request)
  request.catch(() => {
    if (detailPromises.get(key) === request) detailPromises.delete(key)
  })
  return request
}

export function probabilityExplanation(probability: number | undefined, locale: PresentationLocale = 'zh-CN') {
  if (probability === undefined) return tForLocale(locale, 'common.probabilityNoData')
  if (probability < 0.2) return tForLocale(locale, 'common.probabilityLow')
  if (probability < 0.4) return tForLocale(locale, 'common.probabilityBelowAverage')
  if (probability < 0.6) return tForLocale(locale, 'common.probabilityUnclear')
  if (probability < 0.8) return tForLocale(locale, 'common.probabilityLikely')
  return tForLocale(locale, 'common.probabilityHigh')
}

export function resetPresentationCache() {
  rankingPromise = null
  homeBundlePromise = null
  catalogManifestPromise = null
  detailsIndexPromise = null
  searchBundlePromise = null
  homeFeedPromises.clear()
  catalogPagePromises.clear()
  detailsPagePromises.clear()
  catalogFeedPromises.clear()
  detailPromises.clear()
  localeBundlePromises.clear()
}
